import { parseISO, type ISODate, type PlanItem, type PlanSlot } from "@clarent/scheduling";
import { formatDate, formatDateLong, formatHours, plural } from "./format";
import { PROCESS_META, processKind } from "./process";

const weekdayDay = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", timeZone: "UTC" });

/** "Tue 3 – Thu 5 Nov", "Fri 30 Oct – Tue 3 Nov", or one date when they match. */
export function formatRange(start: ISODate, end: ISODate): string {
  if (start === end) return formatDateLong(start);
  if (start.slice(0, 7) === end.slice(0, 7)) return `${weekdayDay.format(new Date(parseISO(start)))} – ${formatDateLong(end)}`;
  return `${formatDate(start)} – ${formatDateLong(end)}`;
}

/** Design system counts: "6 working days from today", "Today", "3 working days ago". */
export function workingDaysFromToday(slack: number): string {
  if (slack === 0) return "Today";
  return slack > 0 ? `${plural(slack, "working day")} from today` : `${plural(-slack, "working day")} ago`;
}

export function planItemTitle(item: PlanItem): string {
  switch (item.kind) {
    case "machining":
      return "Machining";
    case "courier_out":
      return "Courier out";
    case "courier_back":
      return "Courier back";
    case "process":
      return item.name ?? "Outside process";
  }
}

/**
 * The second line of a plan row: "80 h on M1, 70 h on M2 = 11 shop days", "1 working day",
 * "5 calendar days, weekends count". `machineName` turns a machine id into what the shop calls it.
 */
export function planItemDetail(item: PlanItem, machineName: (machineId: string) => string): string {
  switch (item.kind) {
    case "machining": {
      const split = (item.machines ?? []).map((m) => `${formatHours(m.hours)} on ${machineName(m.machineId)}`).join(", ");
      return `${split || formatHours(item.hours ?? 0)} = ${plural(item.days, "shop day")}`;
    }
    case "courier_out":
    case "courier_back":
      return plural(item.days, "working day");
    case "process":
      return `${plural(item.days, "calendar day")}, weekends count`;
  }
}

/** Colour classes for a plan item or slot: dot/marker, solid bar and its text. */
export function slotStyle(slot: Exclude<PlanSlot, null>, items: readonly PlanItem[]): { dot: string; bar: string } {
  switch (slot.kind) {
    case "machining":
      return { dot: "bg-ink", bar: "bg-ink text-white" };
    case "courier_out":
    case "courier_back":
      return { dot: "bg-quoted", bar: "courier-box" };
    case "due":
      return { dot: "bg-ink", bar: "bg-risk text-white" };
    case "process": {
      const kind = processKind(items[slot.item]?.name ?? "");
      return { dot: PROCESS_META[kind].dot, bar: `${PROCESS_META[kind].solid} text-white` };
    }
  }
}
